import { getCurrentInstance } from 'vue';
import { useNuxtApp, useRouter } from 'nuxt/app';
import { useUserInterfaceStore } from '~~/common/stores/userInterface';

export const ID_HEADER = Symbol('idHeader');
export const ID_MAIN = Symbol('idMain');
export const ID_BASKET_SPLIT = Symbol('idBasketSplit');
export const ID_BASKET_TOP = Symbol('idBasketTop');
export const ID_CHECKOUT_TOP = Symbol('idCheckoutTop');
export const ID_CHECKOUT_CUSTOMER = Symbol('idCheckoutCustomer');
export const ID_CHECKOUT_DELIVERY_INFO = Symbol('idCheckoutDeliveryInfo');
export const ID_CHECKOUT_DELIVERY_DATA = Symbol('idCheckoutDeliveryData');
export const ID_CHECKOUT_BILLING_INFO = Symbol('idCheckoutBillingInfo');
export const ID_CHECKOUT_PAYMENT_INFO = Symbol('idCheckoutPaymentInfo');
export const ID_PRODUCT_TOP = Symbol('idProductTop');
export const ID_PRODUCT_STOCK_INFORMATION = Symbol('idProductStockInformation');
export const ID_PRODUCT_REVIEWS = Symbol('idProductReviews');
export const ID_CATEGORY_TOP = Symbol('idCategoryTop');
export const ID_PRODUCT_LIST_TOP = Symbol('idProductListTop');
export const ID_SEARCH_LIST_TOP = Symbol('idSearchListTop');
export const ID_CMS = Symbol('idCmsTop');
export const ID_FAQ_TOP = Symbol('idFaqTop');

export const Navigation = {
  [ID_HEADER]: 'aw_header',
  [ID_MAIN]: 'aw_main',
  [ID_BASKET_SPLIT]: (checkoutType: string): string => `basket_${checkoutType}`,
  [ID_BASKET_TOP]: 'basket_top',
  [ID_CHECKOUT_TOP]: 'checkout_top',
  [ID_CHECKOUT_CUSTOMER]: 'checkout_customer',
  [ID_CHECKOUT_DELIVERY_INFO]: 'checkout_delivery_info',
  [ID_CHECKOUT_DELIVERY_DATA]: 'checkout_delivery_data',
  [ID_CHECKOUT_BILLING_INFO]: 'checkout_billing_info',
  [ID_CHECKOUT_PAYMENT_INFO]: 'checkout_payment_info',
  [ID_PRODUCT_TOP]: 'product_top',
  [ID_PRODUCT_STOCK_INFORMATION]: 'product_stock_information',
  [ID_PRODUCT_REVIEWS]: 'product_reviews',
  [ID_CATEGORY_TOP]: 'category_top',
  [ID_PRODUCT_LIST_TOP]: 'product_list_top',
  [ID_SEARCH_LIST_TOP]: 'search_list_top',
  [ID_CMS]: 'cms_top',
  [ID_FAQ_TOP]: 'faq_top',
};

export function useFocusRef () {
  const $router = useRouter();
  const $logger = useNuxtApp().$logger;
  const currentInstance = getCurrentInstance();
  return focusRef.bind(
    null,
    {
      $router,
      $logger,
      $refs: () => currentInstance?.refs as RefsRecord | undefined,
    },
  );
}

type RefElement = HTMLElement | { $el: HTMLElement };
type RefsRecord = Record<string, RefElement>;

export function focusRef (/* VueComponent */{
  $router = useRouter(),
  $logger = useNuxtApp().$logger,
  $refs,
}: {
  $router?: ReturnType<typeof useRouter>;
  $logger?: ReturnType<typeof useNuxtApp>['$logger'];
  $refs: (() => RefsRecord | undefined) | RefsRecord;
}, /* Config */{
  refId,
  focusElementRefId,
  scrollElementRef,
}: {
  refId: string;
  focusElementRefId?: string;
  scrollElementRef?: HTMLElement;
}) {
  const refs = typeof $refs === 'function' ? $refs() : $refs;
  const { isBodyLocked } = useUserInterfaceStore();
  const routeHash = $router.currentRoute.value.hash;
  if (!routeHash || routeHash === `#${refId}`) {
    if (refs?.[refId]) {
      // ref can be both a VueComponent instance or a HTMLElement instance.
      const refElement = refs[refId];
      const navigationElement: HTMLElement = (refElement as { $el: HTMLElement }).$el || (refElement as HTMLElement);
      if (!isBodyLocked) {
        focusAndScrollToElement(navigationElement);
      }
    } else if (focusElementRefId && refs?.[focusElementRefId] && scrollElementRef) {
      const refElement = refs[focusElementRefId];
      const navigationFocusElement = (refElement as { $el: HTMLElement }).$el || (refElement as HTMLElement);
      const navigationScrollElement = scrollElementRef || document.body;
      if (!isBodyLocked) {
        focusElement(navigationFocusElement);
        scrollToElement({ navigationElement: navigationScrollElement });
      }
    } else {
      $logger.error(`Autofocus failed, target not in refs: ${refId}, ${focusElementRefId}, ${scrollElementRef}.`);
    }
  }
}

export function focusAndScrollToElement (navigationElement: HTMLElement) {
  if (navigationElement && document.activeElement !== navigationElement) {
    setTabIndexToBeFocusable(navigationElement);
    navigationElement.focus();
    scrollToElement({ navigationElement });
  }
}

export function focusElement (navigationElement: HTMLElement) {
  if (navigationElement && document.activeElement !== navigationElement) {
    setTabIndexToBeFocusable(navigationElement);
    navigationElement.focus();
  }
}

function setTabIndexToBeFocusable (navigationElement: HTMLElement) {
  if (navigationElement.getAttribute('tabindex') === null) {
    navigationElement.setAttribute('tabindex', '-1');
  }
}

function scrollToElement ({ navigationElement }: { navigationElement: HTMLElement }) {
  const heightOfHeader = document.getElementById('aw_header')?.offsetHeight || 0;
  const navigationRect = navigationElement.getBoundingClientRect();
  const viewportRectNotHiddenByFloatingHeader = DOMRect.fromRect({
    y: heightOfHeader,
    width: window.innerWidth,
    height: window.innerHeight - heightOfHeader,
  });
  if (isTwoRectsDisjoint(viewportRectNotHiddenByFloatingHeader, navigationRect)) {
    window.scrollTo({
      top: navigationRect.top + window.pageYOffset - heightOfHeader,
      left: navigationRect.left + window.pageXOffset,
      behavior: 'smooth',
    });
  }
}

function isTwoRectsDisjoint (rect1: DOMRect, rect2: DOMRect) {
  return (
    rect1.right < rect2.left ||
      rect2.right < rect1.left ||
      rect1.bottom < rect2.top ||
      rect2.bottom < rect1.top
  );
}

/*export function scrollToElement (navigationElement) {
  const {
    top: elementTop,
    left: elementLeft,
  } = navigationElement.getBoundingClientRect();

  window.scrollTo({
    top: elementTop + window.pageYOffset - document.getElementById(Navigation[ID_HEADER])?.offsetHeight,
    left: elementLeft + window.pageXOffset,
    behavior: 'smooth',
  });
}*/
